import { Component, EventEmitter, Input, OnInit, Output, inject } from "@angular/core";
import { Utilities } from "@tylertech/shared-module";
import { GridMetadata, IMetadataAction } from "@tylertech/tyler-api-types";
import { MetadataService, MetadataActionService } from "../../services";
import { Command, LabelCommand } from "@tylertech/commands-module";
import { ActionCallBackArgs } from "../metadataForm/metadataAction.component";
@Component({
    selector: "metadata-panel-grid-actions",
    templateUrl: "./metadataPanelGridActions.tmpl.html",
    standalone: false
})
export class MetadataPanelGridActionComponent implements OnInit
{
    private readonly $metadataService = inject(MetadataService);
    private $metadataActionService = inject(MetadataActionService);

    @Input()
    model: any;

    @Input()
    metadata: GridMetadata;

    @Input()
    viewOnly: boolean = false;

    @Input()
    index: number;

    @Input()
    canAdd: boolean;

    @Input()
    hasEditFunction: boolean = false;

    @Output()
    public deleteAction = new EventEmitter<number>();

    @Output()
    public copyAction = new EventEmitter<number>();

    @Output()
    public customFunction = new EventEmitter<number>();

    @Output()
    afterAction = new EventEmitter<void>();

    commands: Command[];
    primaryActions: IMetadataAction[];
    overflowActions: IMetadataAction[];

    ngOnInit(): void
    {
        if (!Utilities.IsNullOrUndefined(this.metadata.Actions))
        {
            this.primaryActions = this.metadata.Actions.filter(
                x =>
                    x.Primary && !x.Hidden && !this.$metadataService.EvaluateBoolean(x.HiddenProperty, null, this.model)
            );
            this.overflowActions = this.metadata.Actions.filter(
                x =>
                    !x.Primary &&
                    !x.Hidden &&
                    !this.$metadataService.EvaluateBoolean(x.HiddenProperty, null, this.model)
            );
        }
        if (this.hasOverflowActions)
        {
            this.loadCommands();
        }
    }

    private loadCommands(): void
    {
        this.commands = [];
        if (this.canCopy())
        {
            this.commands.push(
                new LabelCommand({
                    label: "Copy",
                    action: _$event => this.copy(),
                    getEnabled: () => true,
                })
            );
        }
        if (!Utilities.IsNullOrUndefined(this.metadata.Actions))
        {
            this.overflowActions.forEach(action =>
            {
                this.commands.push(
                    new LabelCommand({
                        label: action.Label,
                        action: $event => this.performCommand(this.model, action, $event),
                        getEnabled: () => !action.Disabled,
                        tooltip: action.Label,
                        getVisible: () => !action.Hidden,
                    })
                );
            });
        }
    }

    afterActionCallback(arg: ActionCallBackArgs): void
    {
        this.afterAction.emit();
    }

    canEdit(): boolean
    {
        if (Utilities.IsNullOrUndefined(this.metadata))
        {
            return false;
        }
        if (this.model.SerializedRowState === "Added" && this.canAdd)
        {
            return true;
        }
        const itemEnabled =
            Utilities.IsNullOrUndefined(this.model) ||
            Utilities.IsNullOrUndefinedOrEmpty(this.metadata.CanEditProperty) ||
            this.$metadataService.EvaluateBoolean(this.metadata.CanEditProperty, null, this.model);
        return !this.viewOnly && this.metadata.CanEdit && itemEnabled;
    }

    canDelete(): boolean
    {
        if (Utilities.IsNullOrUndefined(this.metadata)) return false;
        const itemEnabled =
            Utilities.IsNullOrUndefined(this.model) ||
            Utilities.IsNullOrUndefinedOrEmpty(this.metadata.CanDeleteProperty) ||
            this.$metadataService.EvaluateBoolean(this.metadata.CanDeleteProperty, null, this.model);
        return this.showDelete() && itemEnabled;
    }

    showDelete(): boolean
    {
        return !this.viewOnly && this.metadata.CanDelete;
    }

    canCopy(): boolean
    {
        if (Utilities.IsNullOrUndefined(this.metadata))
        {
            return false;
        }
        const itemEnabled =
            Utilities.IsNullOrUndefined(this.model) ||
            Utilities.IsNullOrUndefinedOrEmpty(this.metadata.CanCopyProperty) ||
            this.$metadataService.EvaluateBoolean(this.metadata.CanCopyProperty, null, this.model);
        return !this.viewOnly && this.metadata.CanCopy && itemEnabled;
    }

    hasOverflowActions(): boolean
    {
        const overflowActions = Utilities.IsNullOrUndefined(this.metadata.Actions)
            ? null
            : this.metadata.Actions.filter(
                x =>
                    !x.Primary &&
                    !x.Hidden &&
                    !this.$metadataService.EvaluateBoolean(x.HiddenProperty, null, this.model)
            );
        return this.canCopy() || (!Utilities.IsNullOrUndefined(overflowActions) && overflowActions.length > 0);
    }

    copy(): void
    {
        if (Utilities.IsNullOrUndefined(this.copyAction))
        {
            return;
        }
        this.copyAction.emit(this.index);
    }

    fireCustomFunction(): void
    {
        if (Utilities.IsNullOrUndefined(this.customFunction))
        {
            return;
        }
        this.customFunction.emit(this.index);
    }

    delete(): void
    {
        if (Utilities.IsNullOrUndefined(this.deleteAction))
        {
            return;
        }
        this.deleteAction.emit(this.model);
    }

    performCommand(model: any, action: IMetadataAction, $event?: Event): void
    {
        const self = this;
        if (!Utilities.IsNullOrUndefined($event))
        {
            $event.stopPropagation();
        }
        this.$metadataActionService.ExecuteAction(model, action, null).then(_r =>
        {
            self.afterAction.emit();
        });
    }
}
